import { useCallback, useEffect, useRef, useState } from "react";

export type Facing = "user" | "environment";

export type DeviceOption = {
  deviceId: string;
  label: string;
  kind: "videoinput" | "audioinput";
};

export type ZoomCaps = {
  supported: boolean;
  min: number;
  max: number;
  step: number;
  value: number;
};

export type CameraState = {
  stream: MediaStream | null;
  videoDevices: DeviceOption[];
  audioDevices: DeviceOption[];
  activeVideoId: string | null;
  activeAudioId: string | null;
  facing: Facing;
  zoom: ZoomCaps;
  micEnabled: boolean;
  camEnabled: boolean;
  error: string | null;
  loading: boolean;
};

type Options = {
  // Called when the video track changes (start, switch). The caller uses this
  // to replaceTrack on any active WebRTC sender so we don't re-negotiate SDP.
  onVideoTrackChanged?: (track: MediaStreamTrack) => void | Promise<void>;
  onAudioTrackChanged?: (track: MediaStreamTrack) => void | Promise<void>;
};

function defaultVideoConstraints(): MediaTrackConstraints {
  return {
    frameRate: { ideal: 30 },
  };
}

export function useCamera(opts: Options = {}) {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [videoDevices, setVideoDevices] = useState<DeviceOption[]>([]);
  const [audioDevices, setAudioDevices] = useState<DeviceOption[]>([]);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [activeAudioId, setActiveAudioId] = useState<string | null>(null);
  const [facing, setFacing] = useState<Facing>("user");
  const [zoom, setZoom] = useState<ZoomCaps>({
    supported: false, min: 1, max: 1, step: 0.1, value: 1,
  });
  const [micEnabled, setMicEnabled] = useState(true);
  const [camEnabled, setCamEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Keep a ref in sync so stop() can be called from cleanup
  useEffect(() => { streamRef.current = stream; }, [stream]);

  const enumerate = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const v = devices
        .filter((d) => d.kind === "videoinput")
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Camera ${i + 1}`,
          kind: "videoinput" as const,
        }));
      const a = devices
        .filter((d) => d.kind === "audioinput")
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${i + 1}`,
          kind: "audioinput" as const,
        }));
      setVideoDevices(v);
      setAudioDevices(a);
    } catch (e) {
      console.warn("enumerateDevices failed", e);
    }
  }, []);

  const readZoomCaps = useCallback((track: MediaStreamTrack): ZoomCaps => {
    type ZoomCapability = { min: number; max: number; step?: number };
    type CapabilitiesWithZoom = MediaTrackCapabilities & { zoom?: ZoomCapability };
    type SettingsWithZoom = MediaTrackSettings & { zoom?: number };

    const caps = (track.getCapabilities?.() ?? {}) as CapabilitiesWithZoom;
    const settings = (track.getSettings?.() ?? {}) as SettingsWithZoom;

    if (caps.zoom && typeof caps.zoom.min === "number" && typeof caps.zoom.max === "number") {
      return {
        supported: caps.zoom.max > caps.zoom.min,
        min: caps.zoom.min,
        max: caps.zoom.max,
        step: caps.zoom.step ?? 0.1,
        value: settings.zoom ?? caps.zoom.min,
      };
    }
    return { supported: false, min: 1, max: 1, step: 0.1, value: 1 };
  }, []);

  const startWithConstraints = useCallback(
    async (videoConstraints: MediaTrackConstraints) => {
      setLoading(true);
      setError(null);
      try {
        let next: MediaStream;
        try {
          next = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints,
            audio: true,
          });
        } catch (firstError) {
          const hasPinnedCamera =
            typeof videoConstraints.deviceId !== "undefined" ||
            typeof videoConstraints.facingMode === "undefined";
          if (hasPinnedCamera) throw firstError;

          // Fall back to a plain camera request so devices that dislike a
          // strict facing-mode hint still get a usable preview.
          try {
            next = await navigator.mediaDevices.getUserMedia({
              video: defaultVideoConstraints(),
              audio: true,
            });
          } catch (audioError) {
            next = await navigator.mediaDevices.getUserMedia({
              video: defaultVideoConstraints(),
              audio: false,
            });
            if (audioError instanceof Error) {
              console.warn("microphone unavailable, continuing with video only", audioError.message);
            }
          }
        }

        // Stop old tracks (after grabbing the new stream so the preview never blanks).
        const old = streamRef.current;
        setStream(next);

        const vTrack = next.getVideoTracks()[0];

        if (vTrack) {
          const settings = vTrack.getSettings();
          setActiveVideoId(settings.deviceId ?? null);
          if (typeof settings.facingMode === "string") {
            setFacing(settings.facingMode === "environment" ? "environment" : "user");
          }
          setZoom(readZoomCaps(vTrack));
          await opts.onVideoTrackChanged?.(vTrack);
        }

        const aTrack = next.getAudioTracks()[0];
        if (aTrack) {
          setActiveAudioId(aTrack.getSettings().deviceId ?? null);
          setMicEnabled(aTrack.enabled);
          await opts.onAudioTrackChanged?.(aTrack);
        } else {
          setActiveAudioId(null);
          setMicEnabled(false);
        }

        old?.getTracks().forEach((t) => t.stop());

        // Now that the user has granted permission, labels will be populated.
        await enumerate();
        return next;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Camera/mic access denied");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [enumerate, opts, readZoomCaps]
  );

  const start = useCallback(async (videoConstraints?: MediaTrackConstraints) => {
    const base = defaultVideoConstraints();
    const video: MediaTrackConstraints = videoConstraints
      ? { ...base, ...videoConstraints }
      : { ...base, facingMode: { ideal: facing } };
    if (videoConstraints && typeof videoConstraints.facingMode === "undefined" && typeof videoConstraints.deviceId === "undefined") {
      video.facingMode = { ideal: facing };
    }
    return startWithConstraints(video);
  }, [facing, startWithConstraints]);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setStream(null);
    streamRef.current = null;
  }, []);

  const switchToVideoDevice = useCallback(async (deviceId: string) => {
    await startWithConstraints({ ...defaultVideoConstraints(), deviceId: { exact: deviceId } });
  }, [startWithConstraints]);

  const switchToAudioDevice = useCallback(async (deviceId: string) => {
    const current = streamRef.current;
    const videoTrack = current?.getVideoTracks()[0];
    if (!current || !videoTrack) {
      setActiveAudioId(deviceId);
      return;
    }

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
        video: false,
      });
      const nextAudio = audioStream.getAudioTracks()[0];
      if (!nextAudio) return;

      const next = new MediaStream([videoTrack, nextAudio]);
      current.getAudioTracks().forEach((track) => track.stop());
      setStream(next);
      streamRef.current = next;
      setActiveAudioId(nextAudio.getSettings().deviceId ?? deviceId);
      setMicEnabled(nextAudio.enabled);
      await opts.onAudioTrackChanged?.(nextAudio);
      await enumerate();
    } catch (e) {
      console.warn("switchToAudioDevice failed", e);
      setError(e instanceof Error ? e.message : "Unable to switch microphone");
    }
  }, [enumerate, opts]);

  const flipCamera = useCallback(async () => {
    const nextFacing: Facing = facing === "user" ? "environment" : "user";
    setFacing(nextFacing);
    await startWithConstraints({ ...defaultVideoConstraints(), facingMode: { ideal: nextFacing } });
  }, [facing, startWithConstraints]);

  const setZoomLevel = useCallback(async (next: number) => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track || !zoom.supported) return;
    const clamped = Math.max(zoom.min, Math.min(zoom.max, next));
    try {
      await track.applyConstraints({
        advanced: [{ zoom: clamped } as MediaTrackConstraintSet & { zoom: number }],
      });
      setZoom((z) => ({ ...z, value: clamped }));
    } catch (e) {
      console.warn("zoom applyConstraints failed", e);
    }
  }, [zoom.max, zoom.min, zoom.supported]);

  const toggleMic = useCallback(() => {
    const a = streamRef.current?.getAudioTracks()[0];
    if (!a) return;
    a.enabled = !a.enabled;
    setMicEnabled(a.enabled);
  }, []);

  const toggleCam = useCallback(() => {
    const v = streamRef.current?.getVideoTracks()[0];
    if (!v) return;
    v.enabled = !v.enabled;
    setCamEnabled(v.enabled);
  }, []);

  // Refresh device list when devices are plugged/unplugged
  useEffect(() => {
    const handler = () => { void enumerate(); };
    navigator.mediaDevices.addEventListener?.("devicechange", handler);
    return () => navigator.mediaDevices.removeEventListener?.("devicechange", handler);
  }, [enumerate]);

  const state: CameraState = {
    stream, videoDevices, audioDevices, activeVideoId, activeAudioId,
    facing, zoom, micEnabled, camEnabled, error, loading,
  };

  return {
    state,
    start,
    stop,
    flipCamera,
    switchToVideoDevice,
    switchToAudioDevice,
    setZoomLevel,
    toggleMic,
    toggleCam,
  };
}
